import React, { useEffect, useState, forwardRef, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  Avatar,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fade,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StarIcon from "@mui/icons-material/Star";
import WbIncandescentIcon from "@mui/icons-material/WbIncandescent";
import DiamondIcon from "@mui/icons-material/Diamond";
import { motion } from "framer-motion";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "../api/firebase";
import { keyframes } from "@emotion/react";

/* ───────── ステップ色 & アイコン ───────── */
const stepColors = {
  1: "#c77dff",
  2: "#5ad8ff",
  3: "#9effa1",
  4: "#faff7d",
  5: "#ffb454",
  6: "#ff6b6b",
  7: "#ff8bd4",
  8: "#a5a5ff",
};
const icons = [
  FavoriteIcon,
  StarIcon,
  AutoAwesomeIcon,
  WbIncandescentIcon,
  DiamondIcon,
];

/* ✨ 上昇キラキラのキーフレーム */
const sparkleRise = keyframes`
  0%   { opacity:0; transform:scale(0) translateY(0); }
  20%  { opacity:.9; transform:scale(.8) translateY(-8px); }
  100% { opacity:0; transform:scale(1.1) translateY(-36px); }
`;

/* MUI Fade をそのまま使うトランジション */
const Transition = forwardRef(function Transition(props, ref) {
  return <Fade ref={ref} timeout={600} {...props} />;
});

/* カードごとに一度だけ乱数を作る */
export const Sparkles = React.memo(() => {
  const specs = useMemo(
    () =>
      [...Array(6)].map(() => ({
        left: `${10 + Math.random() * 80}%`,
        delay: `${Math.random() * 2.4}s`,
        size: 4 + Math.random() * 4,
      })),
    []
  );

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      {specs.map(({ left, delay, size }, i) => (
        <Box
          key={i}
          component="span"
          sx={{
            position: "absolute",
            bottom: 4,
            left,
            width: size,
            height: size,
            borderRadius: "50%",
            background: "#ffd54f",
            boxShadow: "0 0 6px 2px #ffd54faa",
            animation: `${sparkleRise} 2.4s linear infinite`,
            animationDelay: delay,
          }}
        />
      ))}
    </Box>
  );
});

/* ① ここで burst を宣言 → ★↓↓ Star が参照 ★ */
const burst = keyframes`
  0%   { opacity:1; transform:scale(.4) translateY(0); }
  80%  { opacity:1; }
  100% { opacity:0; transform:scale(1.2) translateY(-16px); }
`;
/* 入力エリア */
const TypeBox = ({ value, onChange, placeholder, clr }) => {
  const [stars, setStars] = React.useState([]);

  /* 文字を打つたびに星を追加 */
  // const handleChange = (e) => {
  //   const newVal = e.target.value;
  //   onChange(newVal); // ← ここ！eventではなく value を渡す！
  //   // 星エフェクト処理
  //   const x = 10 + Math.random() * 90 + "%";
  //   const y = 60 + Math.random() * 20 + "%";
  //   const id = Date.now();
  //   setStars((prev) => [...prev, { id, x, y }]);
  //   setTimeout(() => setStars((p) => p.filter((s) => s.id !== id)), 800);
  // };

  const handleChange = (e) => {
       onChange(e.target.value); // 値だけ渡す
    };

  return (
    <Box
      sx={{
        position: "relative",
        mt: 1,
        "& textarea": {
          width: "100%",
          minHeight: 120,
          padding: "16px", // ← これに変更！
          borderRadius: 2,
          bgcolor: "rgba(255,255,255,0.06)",
          border: `1px solid ${clr}55`,
          color: "#ffd54f" /* ✨ 黄金文字 ✨ */,
          resize: "vertical",
          fontFamily: "inherit",
        },
      }}
    >
      <textarea
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
      />
      {/* 星を重ね描画 */}
      {stars.map((s) => (
        <Star key={s.id} x={s.x} y={s.y} />
      ))}
    </Box>
  );
};
/* 1 つの星 */
const Star = ({ x, y }) => (
  <StarIcon
    sx={{
      position: "absolute",
      left: x,
      top: y,
      fontSize: 14,
      color: "#ffd54f", // ← 黄金色に固定
      // pointerEvents: "none",
      animation: `${burst} .8s ease-out forwards`,
    }}
  />
);

/* --- きらめきアニメ --- */
const twinkle = keyframes`
  0%, 100% { opacity: .8; }
  50%      { opacity: .2; }
`;

/* --- 流れ星アニメ（左上→右下へ）--- */
const shooting = keyframes`
  0%   { opacity: 0; transform: translate3d(0,0,0) rotate(45deg); }
  5%   { opacity: 1; }
  100% { opacity: 0; transform: translate3d(800px,800px,0) rotate(45deg); }
`;

/* ★ 背景星と流れ星をまとめたコンポーネント */
const BackgroundSky = () => {
  /* 星座ページ全体に 300 個の星を固定配置 */
  const stars = useMemo(
    () =>
      [...Array(300)].map(() => {
        const size = Math.random() * 2 + 0.5;
        return {
          x: Math.random() * 100,
          y: Math.random() * 100,
          size,
          twDur: Math.random() * 8 + 4, // 点滅周期
          driftDur: Math.random() * 120 + 60, // 60〜180s で往復
          dx: (Math.random() < 0.5 ? -1 : 1) * (20 + Math.random() * 30) + "vw",
        };
      }),
    []
  );

  const DIRECTIONS = [
    { deg: 45, dx: 800, dy: 800 }, // ↘
    { deg: 135, dx: -800, dy: 800 }, // ↙
    { deg: 225, dx: -800, dy: -800 }, // ↖
    { deg: 315, dx: 800, dy: -800 }, // ↗
  ];

  /* 流れ星を state で管理：一定間隔で追加→5s後に削除 */
  const [shooters, setShooters] = useState([]);
  useEffect(() => {
    const spawn = () => {
      const dir = DIRECTIONS[Math.floor(Math.random() * DIRECTIONS.length)];
      const id = Date.now();
      setShooters((p) => [...p, { id, dir }]);
      setTimeout(() => setShooters((p) => p.filter((s) => s.id !== id)), 5000);
    };
    /* 8〜12秒おきに 1 本発生 */
    const timer = setInterval(spawn, 2000 + Math.random() * 2000);
    Array.from({ length: 3 }).forEach(spawn);

    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: -1, // ★ 背面へ
        background: "radial-gradient(ellipse at bottom, #0b0021 0%, #000 100%)",
        overflow: "hidden",
      }}
    >
      {/* 小さな星たち */}
      {stars.map((s, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: `${s.x}vw`,
            top: `${s.y}vh`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            borderRadius: "50%",
            background: "white",
            opacity: 0.8,
            /* 点滅 */
            animation: `${twinkle} ${s.twDur}s infinite,
                  ${drift}   ${s.driftDur}s linear infinite`,
            animationDelay: `${s.twDur / 2}s`,
            /* drift の距離を星ごとに渡す */
            "--dx": s.dx,
          }}
        />
      ))}

      {/* 流れ星たち */}
      {shooters.map(({ id, dir }) => (
        <Box
          key={id}
          sx={{
            position: "absolute",
            left: dir.dx > 0 ? "-100px" : "calc(100% + 100px)",
            top: dir.dy > 0 ? "-100px" : "calc(100% + 100px)",
            width: 2,
            height: 140,
            borderRadius: 2,
            transform: `rotate(${dir.deg}deg)`,
            /* 進行方向に白 → 透明へフェード */
            background: `linear-gradient(${dir.deg}deg,
                    rgba(255,255,255,1) 0%,   /* 進行方向 (先端)=白 */
+                rgba(255,255,255,0) 80%)` /* 後方=透明（尾） */,
            animation: `${shooting} 2s linear`,
            animationFillMode: "forwards",
            "--dx": `${dir.dx}px`,
            "--dy": `${dir.dy}px`,
          }}
        />
      ))}
    </Box>
  );
};

/* 水平方向に 100vw 移動して戻る（往復） */
const drift = keyframes`
  0%   { transform: translateX(0); }
  50%  { transform: translateX(var(--dx)); }
  100% { transform: translateX(0); }
`;

export default function YaeNoInoriPage() {
  const [questions, setQuestions] = useState([]);
  const [answeredIds, setAnsweredIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  /* ───────── ダイアログ状態 ───────── */
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgData, setDlgData] = useState(null); // { q, clr, daysLeft }

  /* 回答入力ダイアログ用 */
  const [answerDlg, setAnswerDlg] = useState({ open: false, q: null, clr: "" });
  const [answerText, setAnswerText] = useState("");

  const [loginAlertOpen, setLoginAlertOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  // 回答表示ダイアログ用
  const [viewAnswerDlg, setViewAnswerDlg] = useState({
    open: false,
    text: "",
    q: null,
    clr: "",
  });

  // 回答データを Map にして保持（id → text）
  const [answerTexts, setAnswerTexts] = useState(new Map());

  /* ───────── ① 取得 ───────── */
  useEffect(() => {
    (async () => {
      const qSnap = await getDocs(
        query(collection(db, "yaeNoInori"), orderBy("openDate", "asc"))
      );
      setQuestions(qSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const user = auth.currentUser;
      if (user) {
        const aSnap = await getDocs(
          collection(db, "users", user.uid, "yaeChikaiAnswers")
        );
        setAnsweredIds(new Set(aSnap.docs.map((d) => d.id)));

        // 🔽 ここを追加！回答内容（text）をMapにして保存
        setAnswerTexts(
          new Map(aSnap.docs.map((d) => [d.id, d.data().text || ""]))
        );
      }

      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const user = auth.currentUser;
      if (!user) return;

      // Firestoreからユーザーのプロフィール情報を取得
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        setUserData(userData);
      }
    })();
  }, []);

  const has3rdGeneration = userData?.payments?.some(
    (p) => p.paymentPhase === "3"
  );

  useEffect(() => {
    /* async 関数を中で宣言して即実行 */
    (async () => {
      // ─── 取得 ───
      const snap = await getDocs(collection(db, "yaeNoInori"));

      // ─── Date 型にそろえて昇順ソート ───
      const qs = snap.docs
        .map((d) => {
          const data = d.data();
          const ts = data.openDate;
          const date = ts?.toDate // Timestamp 型なら toDate()
            ? ts.toDate()
            : new Date(ts); // 文字列なら Date へ
          return { id: d.id, ...data, openDate: date };
        })
        .sort((a, b) => a.openDate - b.openDate); // openDate 昇順

      setQuestions(qs);
    })();
  }, []);

  /* ───────── ② 回答保存 ───────── */
  const handleAnswer = async (q) => {
    const user = auth.currentUser;
    if (!user) return;
    await setDoc(
      doc(db, "users", user.uid, "yaeChikaiAnswers", q.id),
      { answeredAt: new Date() },
      { merge: true }
    );
    setAnsweredIds((prev) => new Set(prev).add(q.id));
  };

  /* ───────── ③ ダイアログ open ───────── */
  const openDialog = (q, clr, daysLeft) => {
    setDlgData({ q, clr, daysLeft });
    setDlgOpen(true);
  };

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#0b0021"
      >
        <CircularProgress />
      </Box>
    );

  const total = questions.length;
  const done = answeredIds.size;

  /* 開放済みカードを押した時 */
  const openAnswerDialog = (q, clr) => {
    const user = auth.currentUser;
    const no = q.no ?? 1;

    if (!user && no > 5) {
      // ⭐ noが6以上で未ログインの場合 → ログイン要求ダイアログを表示
      setLoginAlertOpen(true);
      return;
    }

    if (no >= 6 && !has3rdGeneration) {
      setShowPaymentDialog(true);
      return;
    }

    const existingText = answerTexts.get(q.id) || "";
    setAnswerDlg({ open: true, q, clr });
    setAnswerText(existingText);
  };

  /* 送信 */
  const submitAnswer = async () => {
    const user = auth.currentUser;
    if (!user) {
      setLoginAlertOpen(true); // 👈 ダイアログ表示
      return;
    }

    if (!answerText.trim()) return;

    await setDoc(
      doc(db, "users", user.uid, "yaeChikaiAnswers", answerDlg.q.id),
      {
        answeredAt: new Date(),
        text: answerText.trim(),
      },
      { merge: true }
    );
    setAnsweredIds((prev) => new Set(prev).add(answerDlg.q.id));
    setAnswerDlg({ open: false, q: null, clr: "" });
  };

  return (
    <>
      <div className="mt-12"></div>
      <BackgroundSky />
      <Box
        sx={{
          minHeight: "100vh",
          //   background:
          //     "radial-gradient(circle at 50% 20%, #2a005a 0%, #0b0021 70%)",
          //   color: "white",
          //   py: 6,
          background: "rgba(15,0,40,.6)", // 60% 透過ダークパープル
          color: "white",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            background: "rgba(15,0,40,.85)", // ★ パネルだけ薄紫
            backdropFilter: "blur(4px)",
            p: 3,
            // borderRadius: 2,
          }}
        >
          {/* ───── 進捗バー ───── */}
          <Typography variant="h5" align="center" mb={1}>
            ⭑ {done} / {total}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(done / total) * 100}
            sx={{
              height: 6,
              borderRadius: 3,
              mb: 4,
              "& .MuiLinearProgress-bar": {
                transition: "width 0.4s",
                backgroundColor: "#ffd54f",
              },
            }}
          />

          {/* ───── 88 グリッド ───── */}
          <Grid container spacing={1.5}>
            {questions.map((q, i) => {
              /* ★ 先頭だけ強制入力可にする例 */

              /* 先頭 5 枚 (0,1,2,3,4) を強制入力可にする例 */
              const editableIndices = [0, 1, 2, 3, 4];

              const forcedEditable = editableIndices.includes(i);
              const Icon = icons[i % icons.length];
              const clr = stepColors[q.step] || "#ffffff";
              const isAnswered = answeredIds.has(q.id);

              const now = Date.now();
              const openMs = q.openDate ? new Date(q.openDate).getTime() : 0;
              // const isOpen = !q.openDate || openMs <= now;
              const isOpen = forcedEditable || !q.openDate || openMs <= now;
              const daysLeft = !isOpen
                ? Math.max(1, Math.ceil((openMs - now) / 86_400_000))
                : 0;

              const cardVariants = {
                flipped: { rotateY: 180 },
                base: { rotateY: 0 },
              };

              const showSparkle = isOpen && !isAnswered; // ← キラキラを出す条件

              return (
                <Grid item key={q.id} xs={3} sm={2} md={1}>
                  <motion.div
                    variants={cardVariants}
                    animate={isAnswered ? "flipped" : "base"}
                    transition={{ duration: 0.6 }}
                    style={{ perspective: 800 }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        paddingTop: "100%",
                        transformStyle: "preserve-3d",
                      }}
                    >
                      {/* ───── 表面 ───── */}
                      <Card
                        sx={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: 3,
                          border: `1px solid ${clr}55`,
                          background: "hsla(0 0% 100% / 0.05)",
                          backdropFilter: "blur(6px)",
                          backfaceVisibility: "hidden",
                          transition: "background .25s, border-color .25s",
                          "&:hover": {
                            background: `${clr}33`, // ← ステップ色でふわっと発光
                            borderColor: `${clr}aa`,
                          },
                        }}
                        elevation={0}
                      >
                        <CardActionArea
                          sx={{
                            height: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "transform .25s",
                            "&:hover": { transform: "scale(1.07)" },
                            opacity: isOpen ? 1 : 0.6,
                            transition: "transform .25s, color .25s",
                            "&:hover": {
                              transform: "scale(1.07)",
                              color: "#ffffff",
                            },
                          }}
                          // onClick={async () => {
                          //   if (isOpen) {
                          //     if (isAnswered) {
                          //       // ⭐ 回答済みなら表示ダイアログ
                          //       const user = auth.currentUser;
                          //       if (!user) return;
                          //       const ansDoc = await getDoc(
                          //         doc(
                          //           db,
                          //           "users",
                          //           user.uid,
                          //           "yaeChikaiAnswers",
                          //           q.id
                          //         )
                          //       );
                          //       if (ansDoc.exists()) {
                          //         const data = ansDoc.data();
                          //         setViewAnswerDlg({
                          //           open: true,
                          //           text: data.text ?? "(回答内容なし)",
                          //           q,
                          //           clr,
                          //         });
                          //       }
                          //     } else {
                          //       openAnswerDialog(q, clr); // 未回答 → 入力用ダイアログ
                          //     }
                          //   } else {
                          //     openDialog(q, clr, daysLeft); // 未開放
                          //   }
                          // }}

                          onClick={async () => {
                            if (!isOpen) {
                              openDialog(q, clr, daysLeft); // 未開放カード
                              return;
                            }

                            if (isAnswered) {
                              const user = auth.currentUser;
                              if (user) {
                                const ansDoc = await getDoc(
                                  doc(
                                    db,
                                    "users",
                                    user.uid,
                                    "yaeChikaiAnswers",
                                    q.id
                                  )
                                );
                                if (ansDoc.exists()) {
                                  const data = ansDoc.data();
                                  setViewAnswerDlg({
                                    open: true,
                                    text: data.text ?? "(回答内容なし)",
                                    q,
                                    clr,
                                  });
                                }
                              } else {
                                // ⭐ 回答済みでも no <= 5 なら書き直しOK にしたいならここでもopenAnswerDialogを呼んでもよい
                                openAnswerDialog(q, clr);
                              }
                            } else {
                              openAnswerDialog(q, clr); // 未回答ならそのまま
                            }
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: `${clr}29`,
                              width: 60,
                              height: 60,
                              border: `1px solid ${clr}aa`,
                              color: clr,
                            }}
                          >
                            <Icon fontSize="medium" />
                          </Avatar>
                        </CardActionArea>
                        {/* 残日数バッジ */}
                        {!isOpen && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 4,
                              right: 6,
                              fontSize: 12,
                              opacity: 0.8,
                              color: clr,
                            }}
                          >
                            あと{daysLeft}日
                          </Box>
                        )}
                        {/* ✨ sparkle overlay — 開放済み・未回答カードだけ */}
                        {isOpen && !isAnswered && (
                          <Sparkles /* jsx */
                            sx={{
                              zIndex: -1,
                            }} /* 追加：MUI sx も渡せるようメモ */
                          />
                        )}{" "}
                      </Card>

                      {/* ───── 裏面（回答済み）───── */}
                      <Card
                        sx={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: 3,
                          border: `1px solid ${clr}aa`,
                          background: `linear-gradient(135deg, ${clr}40, ${clr}10)`,
                          backdropFilter: "blur(8px)",
                          transform: "rotateY(180deg)",
                          backfaceVisibility: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: `0 0 12px 2px ${clr}66`,
                        }}
                        elevation={0}
                      >
                        <motion.div
                          initial={{ opacity: 0, scale: 0.6 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.6 }}
                          style={{
                            position: "absolute",
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            boxShadow: `0 0 8px 2px ${clr}`,
                            filter: "blur(2px)",
                          }}
                        />
                        <Typography variant="h5" sx={{ color: clr }}>
                          ★
                        </Typography>
                      </Card>
                    </Box>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Container>

        {/* ───────── 期日未到達ダイアログ ───────── */}
        {dlgData && (
          <Dialog
            open={dlgOpen}
            onClose={() => setDlgOpen(false)}
            TransitionComponent={Transition}
            PaperProps={{
              sx: {
                borderRadius: 4,
                p: 3,
                background: "rgba(25,0,60,0.9)",
                backdropFilter: "blur(12px)",
                border: `1px solid ${dlgData.clr}aa`,
                color: "white",
                maxWidth: 420,
                width: "90%",
              },
            }}
          >
            <DialogTitle
              sx={{
                p: 0,
                mb: 2,
                color: dlgData.clr,
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              あと{dlgData.daysLeft}日で解放
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: `${dlgData.clr}55` }}>
              <Typography variant="subtitle1" gutterBottom>
                あなたが得られるもの
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {dlgData.q.gain || "（gain フィールドが未設定）"}
              </Typography>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "center", mt: 2 }}>
              <Button
                onClick={() => setDlgOpen(false)}
                variant="outlined"
                sx={{
                  borderColor: dlgData.clr,
                  color: dlgData.clr,
                  "&:hover": {
                    background: `${dlgData.clr}22`,
                    borderColor: dlgData.clr,
                  },
                }}
              >
                とじる
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* ───────── 回答入力ダイアログ ───────── */}
        {answerDlg.open && (
          <Dialog
            open
            onClose={() => setAnswerDlg({ open: false, q: null, clr: "" })}
            TransitionComponent={Transition}
            PaperProps={{
              sx: {
                borderRadius: 4,
                p: 3,
                background: "rgba(25,0,60,0.9)",
                backdropFilter: "blur(12px)",
                border: `1px solid ${answerDlg.clr}aa`,
                color: "white",
                maxWidth: 480,
                width: "92%",
              },
            }}
          >
            <DialogTitle
              sx={{
                p: 0,
                mb: 2,
                color: answerDlg.clr,
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {answerDlg.q.gain || "祈りに答える"}
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: `${answerDlg.clr}55` }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                color={answerDlg.clr}
              >
                {answerDlg.q.challenge ||
                  "この祈りに対するあなたの答えを書いてください"}
              </Typography>

              {/* <Box
              component="textarea"
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder="ここに入力…"
              style={{
                width: "100%",
                minHeight: 120,
                marginTop: 12,
                padding: 12,
                borderRadius: 8,
                background: "rgba(255,255,255,0.06)",
                border: `1px solid ${answerDlg.clr}55`,
                color: "white",
                resize: "vertical",
              }}
            /> */}
              <TypeBox
                value={answerText}
                onChange={(val) => setAnswerText(val)} // ← 受け取るのは「文字列」
                placeholder="ここに入力..."
                clr={answerDlg.clr}
              />
            </DialogContent>

            <DialogActions sx={{ justifyContent: "center", mt: 2 }}>
              <Button
                onClick={() => setAnswerDlg({ open: false, q: null, clr: "" })}
                sx={{ color: answerDlg.clr }}
              >
                キャンセル
              </Button>
              <Button
                variant="contained"
                onClick={submitAnswer}
                sx={{
                  bgcolor: answerDlg.clr,
                  "&:hover": { bgcolor: answerDlg.clr },
                }}
              >
                送信
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* ログインを促すDialog */}
        <Dialog open={loginAlertOpen} onClose={() => setLoginAlertOpen(false)}>
          <DialogTitle>ご回答にはログインが必要です</DialogTitle>
          <DialogContent>
            {/* <Typography>
              回答するにはログインが必要です。上記「ログイン」もしくは「新規登録」ボタンからログインしてください。
            </Typography> */}
            <Typography sx={{ whiteSpace: "pre-line" }}>
              {
                '上部の「ログイン」または「新規登録」ボタンからログインをお願いいたします。なお、6問目までは無料でお試しいただけます。\n 7問目以降のご回答にはお支払いが必要となりますので、あらかじめご了承ください。\nまた、新規登録後にお支払いが完了していない場合は、お支払いのご案内メールをお送りする場合がございます。'
              }
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLoginAlertOpen(false)}>閉じる</Button>
          </DialogActions>
        </Dialog>

        {/* 支払いを促すDialog */}
        <Dialog
          open={showPaymentDialog}
          onClose={() => setShowPaymentDialog(false)}
        >
          <DialogTitle>お支払いが必要です</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mt: 1 }}>
              クリックされた八重の誓いの回答には、参加費のお支払いが必要です。
              <br />
              上部「お支払」より、お手続きをお願いいたします。
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPaymentDialog(false)}>閉じる</Button>
            {/* <Button
              variant="contained"
              onClick={() => {
                setShowPaymentDialog(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
                // または、必要に応じてお支払いセクションへスクロール
              }}
            >
              お支払いへ
            </Button> */}
          </DialogActions>
        </Dialog>

        <Dialog
          open={viewAnswerDlg.open}
          onClose={() =>
            setViewAnswerDlg({ open: false, text: "", q: null, clr: "" })
          }
          TransitionComponent={Transition}
          PaperProps={{
            sx: {
              borderRadius: 4,
              p: 3,
              background: "rgba(25,0,60,0.9)",
              backdropFilter: "blur(12px)",
              border: `1px solid ${viewAnswerDlg.clr}aa`,
              color: "white",
              maxWidth: 480,
              width: "92%",
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 0,
              mb: 2,
              color: viewAnswerDlg.clr,
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {viewAnswerDlg.q?.gain || "あなたの祈りの答え"}
          </DialogTitle>

          <DialogContent
            dividers
            sx={{ borderColor: `${viewAnswerDlg.clr}55` }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              color={viewAnswerDlg.clr}
            >
              {viewAnswerDlg.q?.challenge || "この祈りに対するあなたの答え"}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mt: 1,
                whiteSpace: "pre-wrap",
                background: "rgba(255,255,255,0.06)",
                border: `1px solid ${viewAnswerDlg.clr}33`,
                borderRadius: 2,
                p: 2,
              }}
            >
              {viewAnswerDlg.text}
            </Typography>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", mt: 2 }}>
            <Button
              onClick={() =>
                setViewAnswerDlg({ open: false, text: "", q: null, clr: "" })
              }
              sx={{ color: viewAnswerDlg.clr }}
            >
              とじる
            </Button>

            <Button
              onClick={() => {
                setViewAnswerDlg({ open: false, text: "", q: null, clr: "" });
                setAnswerDlg({
                  open: true,
                  q: viewAnswerDlg.q,
                  clr: viewAnswerDlg.clr,
                });
                setAnswerText(viewAnswerDlg.text); // ← 既存の回答を渡す
              }}
              sx={{ color: viewAnswerDlg.clr }}
            >
              編集する
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
